import { all, call, put, select } from 'redux-saga/effects';
import { withSagaName } from 'store/withSagaName';
import application from 'features/application/sagas';
import streaming from 'features/streaming/sagas';
import room from 'features/room/sagas';
import layout from 'features/layout/sagas';
import users from 'features/users/sagas';
import recorder from 'features/recorder/sagas';
import devMode from 'features/dev-mode/sagas';
import restreamer from 'features/restreamer/sagas';
import chat from 'features/chat/sagas';
import captions from 'features/transcripts/sagas';
import virtualBackground from 'features/virtual-backgrounds/sagas';
import permissions from 'features/permissions/sagas';
import e2ee from 'features/e2ee/sagas';
import notifications from 'features/notifications/sagas';
import breakoutRooms from 'features/breakout-rooms/sagas';
import qa from 'features/qa/sagas';
import features from 'features/feature-set/sagas';
import * as Sentry from '@sentry/react';
import { roomError } from 'features/room/roomSlice';
import { selectIsRecorderSession } from 'features/recorder/recorderSlice';
import { terminateRecorderSessionSaga } from 'features/recorder/sagas/terminateRecorderSessionSaga';
import { formatErrorForLogging, logger } from 'utils/logger';
import polls from 'features/polls/sagas';
import quizzes from 'features/quizzes/sagas';
import contentLibrary from 'features/content-library/sagas';
import sip from 'features/sip/sagas';
import whiteboard from 'features/whiteboard/sagas';
import toolbar from 'features/toolbar/sagas';
import sdkWatcher from 'features/sdk/sagas';
import screenshare from 'features/screenshare/sagas';
import contentWhiteboard from 'features/content-whiteboard/sagas';
import roomReactions from 'features/room-reactions/sagas';
import contentZoom from 'features/content-zoom/sagas';
import sidePanels from 'features/side-panels/sagas';
import systemStats from 'features/system-stats/sagas';
import localMute from 'features/local-mute/sagas';

const featureSagas: Record<string, () => Generator> = {
  application,
  toolbar,
  streaming,
  screenshare,
  captions,
  room,
  layout,
  users,
  recorder,
  devMode,
  restreamer,
  chat,
  virtualBackground,
  permissions,
  e2ee,
  notifications,
  breakoutRooms,
  qa,
  contentLibrary,
  features,
  polls,
  quizzes,
  whiteboard,
  sip,
  sdkWatcher,
  contentWhiteboard,
  roomReactions,
  contentZoom,
  sidePanels,
  systemStats,
  localMute,
};

function* rootSaga() {
  try {
    yield all(Object.entries(featureSagas).map(([name, saga]) => call(withSagaName, name, saga)));
  } catch (error) {
    const sagaName = (error as { sagaName?: string })?.sagaName ?? 'unknown';
    const { name, message } = formatErrorForLogging(error);

    // Logged before the capture so the attribution also sits in the tail that
    // the terminal close flushes.
    logger.remote({ system: true }).error(`rootSaga: [${sagaName}] ${name}: ${message}`);

    yield put(roomError({ global: true }));
    Sentry.captureException(error, {
      level: 'fatal',
      tags: { saga: sagaName },
    });

    // Every watcher is cancelled by now, and the backend only guards the window
    // before `recordingStarted`, so nothing else would end this recording.
    const isRecorderSession: boolean = yield select(selectIsRecorderSession);
    if (isRecorderSession) {
      yield call(terminateRecorderSessionSaga);
    }
  }
}

export default rootSaga;
